import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@cbm/shared';

const ADMIN_EMAIL = 'anbunathan.r@gmail.com';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

const navigationItems: NavItem[] = [
  {
    name: 'Plant Operator',
    path: '/dashboard/plant-operator',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    roles: ['PLANT'],
  },
  {
    name: 'Plant Manager',
    path: '/dashboard/plant',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    roles: ['PLANT'],
  },
  {
    name: 'OEM Service',
    path: '/dashboard/oem-service',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    roles: ['OEM_SERVICE'],
  },
  {
    name: 'OEM Engineering',
    path: '/dashboard/oem-engineering',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    roles: ['OEM_ENGINEERING'],
  },
  {
    name: 'Quality',
    path: '/dashboard/quality',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    roles: ['QUALITY'],
  },
  {
    name: 'Simulation',
    path: '/simulation',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-5-10v20m5-20v20" />
      </svg>
    ),
    roles: ['PLANT', 'OEM_SERVICE', 'OEM_ENGINEERING'],
  },
];

export function Sidebar() {
  const { user } = useAuth();

  console.log('=== SIDEBAR DEBUG ===');
  console.log('User object:', user);
  console.log('User email:', user?.email);
  console.log('User role:', user?.role);
  console.log('User roles array:', user?.roles);
  console.log('Is admin?', user?.email === ADMIN_EMAIL);

  if (!user) {
    return null;
  }

  const isAdmin = user.email === ADMIN_EMAIL;
  
  const roleMap: Record<string, UserRole[]> = {
    'plant_operator': ['PLANT'],
    'plant_manager': ['PLANT'],
    'oem_service': ['OEM_SERVICE'],
    'oem_engineering': ['OEM_ENGINEERING'],
    'quality': ['QUALITY'],
    'simulation': ['PLANT', 'OEM_SERVICE', 'OEM_ENGINEERING']
  };
  
  const allowedItems = isAdmin 
    ? navigationItems 
    : navigationItems.filter(item => {
        console.log(`Checking item: ${item.name}, required roles:`, item.roles);
        if (item.roles.includes(user.role)) {
          console.log(`  ✓ Matched by user.role: ${user.role}`);
          return true;
        }
        if (user.roles) {
          for (const r of user.roles) {
            console.log(`  Checking user role: ${r}`);
            const mappedRoles = roleMap[r];
            if (mappedRoles) {
              console.log(`    Mapped to:`, mappedRoles);
              if (mappedRoles.some(role => item.roles.includes(role))) {
                console.log(`    ✓ Match found!`);
                return true;
              }
            }
          }
        }
        console.log(`  ✗ No match`);
        return false;
      });

  console.log('Final allowed items:', allowedItems.map(i => i.name));

  return (
    <aside className="w-64 bg-slate-800 border-r border-slate-700 min-h-screen">
      <nav className="mt-6 px-3">
        <div className="space-y-1">
          {allowedItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isActive
                    ? 'bg-slate-700 text-white border-l-2 border-blue-500'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`
              }
            >
              <span className="mr-3 flex-shrink-0">
                {item.icon}
              </span>
              {item.name}
            </NavLink>
          ))}
        </div>
      </nav>
    </aside>
  );
}